/* eslint-disable indent */
import React, { useState, useEffect, useCallback } from "react";
import { Card, Table, DatePicker, Button, Typography } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import moment from "moment";
import { formatIndianNumber, formatIndianCurrency } from "../../../../utils";
import "./style.scss";
import "../style.scss";

const { Title } = Typography;

const ZoneWiseView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [selectedDate, setSelectedDate] = useState(moment());
    const [loading, setLoading] = useState(false);
    const [zoneWiseData, setZoneWiseData] = useState({});
    const [reportType, setReportType] = useState("Piece Rate"); // Default or from location state
    const [reportCategory, setReportCategory] = useState("Business Data"); // Business Data, Shipment Counts, Delivery Data
    const [dataType, setDataType] = useState("today"); // "today" or "range"
    
    const zones = ["EAST", "NORTH", "SOUTH1", "SOUTH2", "WEST1", "WEST2"];

    useEffect(() => {
        // Get report category, type, and data type from location state if available
        const category = location.state?.reportCategory || "Business Data";
        const type = location.state?.reportType || "Piece Rate";
        const typeMode = location.state?.type || "today";
        const businessType = location.state?.businessType || null;
        
        setReportCategory(category);
        setReportType(type);
        setDataType(typeMode);
        
        // If businessType is provided, navigate to ZoneList route instead
        if (businessType) {
            navigate("/zone-list", {
                state: {
                    type: typeMode,
                    reportType: type,
                    reportCategory: category,
                    date: location.state?.date || moment().format("YYYY-MM-DD"),
                    businessType: businessType,
                },
            });
            return;
        }
        
        if (location.state?.date) {
            // Convert string date back to moment object
            const dateValue = typeof location.state.date === "string" 
                ? moment(location.state.date) 
                : moment(location.state.date);
            setSelectedDate(dateValue);
        }
    }, [location.state, navigate]);

    const loadZoneWiseData = useCallback(() => {
        setLoading(true);
        
        // TODO: Replace with actual API call based on reportCategory
        // API call should return data in format based on reportCategory:
        // 
        // For Business Data:
        // {
        //   cash: { EAST: value, NORTH: value, ... },
        //   account: { EAST: value, NORTH: value, ... },
        //   dp: { EAST: value, NORTH: value, ... },
        //   pieceRate: { EAST: value, NORTH: value, ... }
        // }
        //
        // For Shipment Counts:
        // {
        //   cashCount: { EAST: value, NORTH: value, ... },
        //   accountCount: { EAST: value, NORTH: value, ... },
        //   dpCounts: { EAST: value, NORTH: value, ... },
        //   pieceRateCount: { EAST: value, NORTH: value, ... }
        // }
        //
        // For Delivery Data:
        // {
        //   deliveryPercentage: { EAST: value, NORTH: value, ... },
        //   totalDrs: { EAST: value, NORTH: value, ... },
        //   deliveredShipments: { EAST: value, NORTH: value, ... },
        //   ...
        // }
        
        // For now, set empty data structure
        setZoneWiseData({});
        setLoading(false);
    }, [reportCategory, dataType, selectedDate]);

    useEffect(() => {
        // Load data when component mounts or reportCategory/dataType changes
        loadZoneWiseData();
    }, [loadZoneWiseData]);

    const handleDateSubmit = () => {
        loadZoneWiseData();
    };

    const todayDate = selectedDate.format("DD/MM/YYYY");
    const rangeStartDate = selectedDate.startOf("month").format("DD/MM/YYYY");
    const rangeEndDate = selectedDate.format("DD/MM/YYYY");
    const dateRange = `${rangeStartDate} - ${rangeEndDate}`;
    const displayDate = dataType === "today" ? todayDate : dateRange;

    const handleGoBack = () => {
        navigate("/director-dashboardNew");
    };

    // Get row types based on report category
    const getRowTypes = () => {
        if (reportCategory === "Business Data") {
            return [
                { key: "cash", label: "Cash", dataKey: "cash" },
                { key: "account", label: "Account", dataKey: "account" },
                { key: "dp", label: "DP", dataKey: "dp" },
                { key: "pieceRate", label: "Piece Rate", dataKey: "pieceRate" },
            ];
        } else if (reportCategory === "Shipment Counts") {
            return [
                { key: "cashCount", label: "Cash Count", dataKey: "cashCount" },
                { key: "accountCount", label: "Account Count", dataKey: "accountCount" },
                { key: "dpCounts", label: "DP Counts", dataKey: "dpCounts" },
                { key: "pieceRateCount", label: "Piece Rate Count", dataKey: "pieceRateCount" },
            ];
        } else if (reportCategory === "Delivery Data") {
            return [
                { key: "totalDrs", label: "Total DRS", dataKey: "totalDrs" },
                { key: "deliveredShipments", label: "Delivered Shipments", dataKey: "deliveredShipments" },
                { key: "returnShipments", label: "Return Shipments", dataKey: "returnShipments" },
                { key: "cnfdShipments", label: "CNFD Shipments", dataKey: "cnfdShipments" },
                { key: "pendingShipments", label: "Pending Shipments", dataKey: "pendingShipments" },
                { key: "rtoDelivery", label: "RTO Delivery", dataKey: "rtoDelivery" },
            ];
        }
        return [];
    };
    
    // Calculate totals for each row and column
    const calculateRowTotal = (dataKey) => {
        const data = zoneWiseData[dataKey] || {};
        return zones.reduce((sum, zone) => sum + (data[zone] || 0), 0);
    };
    
    const calculateColumnTotal = (zone) => {
        const rowTypes = getRowTypes();
        return rowTypes.reduce((sum, type) => {
            const data = zoneWiseData[type.dataKey] || {};
            return sum + (data[zone] || 0);
        }, 0);
    };
    
    const calculateGrandTotal = () => {
        return zones.reduce((sum, zone) => sum + calculateColumnTotal(zone), 0);
    };

    // Get business types based on report category
    const businessTypes = getRowTypes();
    
    // Zone-wise table columns
    const getColumnTitle = () => {
        if (reportCategory === "Business Data") {
            return "Business";
        } else if (reportCategory === "Shipment Counts") {
            return "Shipment Type";
        } else if (reportCategory === "Delivery Data") {
            return "Delivery Metric";
        }
        return "Type";
    };
    
    // Columns for full view (business types as rows, zones as columns)
    const zoneWiseColumns = [
        {
            title: getColumnTitle(),
            dataIndex: "business",
            key: "business",
            width: 150,
            fixed: "left",
            render: (text) => <strong>{text}</strong>,
        },
        ...zones.map((zone) => ({
            title: zone,
            dataIndex: zone.toLowerCase(),
            key: zone.toLowerCase(),
            align: "right",
            render: (value, record) => {
                const isTotal = record.business === "Total";
                
                return (
                    <span>
                        {value || (isTotal ? "" : "-")}
                    </span>
                );
            },
        })),
        {
            title: "Total",
            dataIndex: "total",
            key: "total",
            align: "right",
            width: 120,
            render: (value) => <strong>{value || ""}</strong>,
        },
    ];
    
    // Format value based on report category and data type
    const formatValue = (value, dataKey) => {
        const numValue = typeof value === "number" ? value : parseFloat(value) || 0;
        
        if (reportCategory === "Business Data") {
            return formatIndianCurrency(numValue);
        } else if (reportCategory === "Delivery Data" && dataKey === "deliveryPercentage") {
            return `${numValue.toFixed(2)}%`;
        } else {
            return formatIndianNumber(numValue);
        }
    };
    
    // Prepare table data source for full view
    const fullTableData = [
        ...businessTypes.map((type) => {
            const data = zoneWiseData[type.dataKey] || {};
            const rowTotal = calculateRowTotal(type.dataKey);
            const rowData = {
                key: type.key,
                business: type.label,
                total: formatValue(rowTotal, type.dataKey),
            };
            
            zones.forEach((zone) => {
                const value = data[zone] || 0;
                rowData[zone.toLowerCase()] = formatValue(value, type.dataKey);
            });
            
            return rowData;
        }),
        {
            key: "total",
            business: "Total",
            total: formatValue(calculateGrandTotal(), "total"),
            ...zones.reduce((acc, zone) => {
                const columnTotal = calculateColumnTotal(zone);
                acc[zone.toLowerCase()] = formatValue(columnTotal, "total");
                return acc;
            }, {}),
        },
    ];

    return (
        <div className="director-dashboard zone-wise-page">
            {/* Breadcrumb */}
            <div className="zone-wise-breadcrumb">
                <Button
                    type="text"
                    icon={<ArrowLeftOutlined />}
                    onClick={handleGoBack}
                    className="back-button"
                >
                    Dashboard
                </Button>
                <span className="breadcrumb-separator">/</span>
                <span className="breadcrumb-current">Business Statistics Report</span>
            </div>

            {/* Date Filter */}
            <Card className="zone-wise-filter-card">
                <div className="zone-wise-date-filter">
                    <label className="zone-wise-date-label">Date:</label>
                    <DatePicker
                        format="DD/MM/YYYY"
                        value={selectedDate}
                        onChange={(date) => setSelectedDate(date || moment())}
                        className="zone-wise-date-picker"
                    />
                    <Button type="primary" onClick={handleDateSubmit} className="zone-wise-submit-btn">
                        Submit
                    </Button>
                </div>
            </Card>

            {/* Zone Wise Table */}
            <Card className="zone-wise-table-card">
                <Title level={4} className="zone-wise-table-title">
                    Zone Wise {reportCategory} - {displayDate} {reportCategory === "Business Data" ? `(${reportType})` : ""}
                </Title>
                <Table
                    columns={zoneWiseColumns}
                    dataSource={fullTableData}
                    pagination={false}
                    loading={loading}
                    size="small"
                    className="zone-wise-table"
                    scroll={{ x: "max-content" }}
                    rowClassName={(record) => {
                        if (record.business === "Total") {
                            return "zone-wise-total-row";
                        }
                        return "";
                    }}
                />
            </Card>
        </div>
    );
};

export default ZoneWiseView;

